/**
 * R3: Cross-seed misplacement [REVIEW QUEUE, computed confidence]
 *
 * Per node (NIE per grupa):
 *   current_j = jaccard(nodeTokens, currentSeedTokens)
 *   For every OTHER seed: alt_j = jaccard(nodeTokens, altSeedTokens)
 *   Best alt = seed z MAX(alt_j)
 *   Jeśli best_alt.j > current_j * 1.5 AND best_alt.j >= 0.35 AND matched_wp_post IS NULL:
 *     confidence = (alt_j - current_j) / alt_j
 *     Push do review queue
 *
 * NIE auto-apply. User akceptuje w UI.
 */

import type {
  RepairRuleInput,
  RepairRuleResult,
  MoveProposal,
  RepairNodeRow,
  RepairSeedRow,
} from "@/types/mapRepair";
import { nodeTokens, seedTokens, jaccardSimilarity } from "./_shared";

const MIN_ABSOLUTE_ALT_JACCARD = 0.35;
const RELATIVE_RATIO = 1.5; // alt musi być ≥ 1.5× lepsze niż current

export function runR3(input: RepairRuleInput): RepairRuleResult {
  const proposals: MoveProposal[] = [];

  // Precompute seed tokens + nazwy
  const nodesBySeed = new Map<string, RepairNodeRow[]>();
  for (const n of input.nodes) {
    const list = nodesBySeed.get(n.seed_id) || [];
    list.push(n);
    nodesBySeed.set(n.seed_id, list);
  }
  const seedTokensById = new Map<string, string[]>();
  const seedsById = new Map<string, RepairSeedRow>();
  for (const seed of input.seeds) {
    seedsById.set(seed.id, seed);
    const scopeNodes = nodesBySeed.get(seed.id) || [];
    seedTokensById.set(seed.id, seedTokens(seed, scopeNodes));
  }

  for (const node of input.nodes) {
    if (node.matched_wp_post_id) continue; // safety — nie ruszamy live URL
    const currentSeed = seedsById.get(node.seed_id);
    if (!currentSeed) continue;

    const nTokens = nodeTokens(node);
    const currentSeedT = seedTokensById.get(node.seed_id) || [];
    const currentJ = jaccardSimilarity(nTokens, currentSeedT);

    let bestAltSeedId: string | null = null;
    let bestAltJ = 0;

    for (const seed of input.seeds) {
      if (seed.id === node.seed_id) continue;
      const altTokens = seedTokensById.get(seed.id) || [];
      const altJ = jaccardSimilarity(nTokens, altTokens);
      if (altJ > bestAltJ) {
        bestAltJ = altJ;
        bestAltSeedId = seed.id;
      }
    }

    if (!bestAltSeedId) continue;
    if (bestAltJ < MIN_ABSOLUTE_ALT_JACCARD) continue;
    if (bestAltJ <= currentJ * RELATIVE_RATIO) continue;

    const proposedSeed = seedsById.get(bestAltSeedId)!;
    const confidence = bestAltJ > 0 ? (bestAltJ - currentJ) / bestAltJ : 0;

    proposals.push({
      node_id: node.id,
      node_slug: node.slug,
      current_seed_id: node.seed_id,
      current_seed_name: currentSeed.name,
      proposed_seed_id: bestAltSeedId,
      proposed_seed_name: proposedSeed.name,
      current_jaccard: Math.round(currentJ * 1000) / 1000,
      alt_jaccard: Math.round(bestAltJ * 1000) / 1000,
      confidence: Math.round(confidence * 1000) / 1000,
    });
  }

  // Sort by confidence desc — najpewniejsze propozycje na górze UI
  proposals.sort((a, b) => b.confidence - a.confidence);

  return {
    rule: "R3",
    operations: [], // zero auto — tylko review queue
    proposals,
    flags: [],
    skipped: [],
  };
}
